import React, { useCallback, useEffect } from 'react';
import { Location, useNavigate } from 'react-router-dom';
import { useNavigationBlocker } from './useNavigationBlocker';
import handleNavigationAway from '../Views/Components/_HumanWritten/AccountDetails/HandleNavigationAway';

export interface useAccountNavigationAwayProps {
	needConfirm: boolean;
	confirmationFunction: () => void;
}

const handleTabClose = (event: BeforeUnloadEvent) => {
	if (!event) return;
	event.preventDefault();
	event.returnValue = '';
};

export function useAccountNavigationAway({ needConfirm, confirmationFunction }: useAccountNavigationAwayProps) {
	const navigate = useNavigate();

	// Intercept all Link clicks before navigation happens
	useEffect(() => {
		if (!needConfirm) return;

		const handleLinkClick = (e: MouseEvent) => {
			// Find if the click was on a link or inside a link
			let target = e.target as HTMLElement | null;
			let link: HTMLAnchorElement | null = null;

			// Traverse up to find an anchor tag
			while (target && target !== document.body) {
				if (target.tagName === 'A') {
					link = target as HTMLAnchorElement;
					break;
				}
				target = target.parentElement;
			}

			// If we found a link and it's a React Router link (not external)
			if (link && link.href && !link.target && !link.download) {
				const linkUrl = new URL(link.href);
				const currentUrl = new URL(window.location.href);

				// Only intercept if it's navigating to a different path
				if (linkUrl.origin === currentUrl.origin && linkUrl.pathname !== currentUrl.pathname) {
					e.preventDefault();
					e.stopPropagation();

					// Show confirmation modal
					handleNavigationAway(needConfirm, confirmationFunction).then(result => {
						if (result) {
							// User confirmed - navigate
							navigate(linkUrl.pathname + linkUrl.search + linkUrl.hash);
						}
					});
				}
			}
		};

		// Use capture phase to intercept before React Router
		document.addEventListener('click', handleLinkClick, true);

		return () => {
			document.removeEventListener('click', handleLinkClick, true);
		};
	}, [needConfirm, confirmationFunction, navigate]);

	// Also keep the blocker for programmatic navigation
	const shouldBlock = useCallback(
		({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
			return needConfirm && currentLocation.pathname !== nextLocation.pathname;
		},
		[needConfirm],
	);

	const blocker = useNavigationBlocker(shouldBlock);

	// Handle blocked navigation attempts
	useEffect(() => {
		if (blocker.state === 'blocked') {
			handleNavigationAway(needConfirm, confirmationFunction).then(result => {
				if (result) {
					blocker.proceed(); // Allow navigation
				} else {
					blocker.reset(); // Cancel navigation
				}
			});
		}
	}, [blocker.state, blocker, needConfirm, confirmationFunction]);

	// Handle browser navigation (refresh, close tab, etc.)
	useEffect(() => {
		if (needConfirm) {
			window.addEventListener('beforeunload', handleTabClose);
		} else {
			window.removeEventListener('beforeunload', handleTabClose);
		}

		return () => {
			window.removeEventListener('beforeunload', handleTabClose);
		};
	}, [needConfirm]);
}
